import { useState, useMemo, useCallback } from 'preact/hooks'
import { AlertTriangle, History, Loader2 } from 'lucide-preact'
import { assetApi, type AssetVersionViewDto } from '@/api/asset'
import { useToast } from '@/hooks/useToast'
import { getDisplayErrorMessages } from '@/utils/errorHandling'

/** A version's stored design this size or smaller is `{}` and can't be restored. */
const EMPTY_DESIGN_BYTES = 2

interface EmptyDesignRecoveryProps {
    clientCompanyId: string
    workspaceId: string
    campaignId: string
    assetId: string
    versions: AssetVersionViewDto[]
    /** Called once a version is back in the draft, so the builder refetches and reopens the editor on it. */
    onRestored: () => Promise<void> | void
}

/**
 * Shown in place of the email template editor when the saved design is empty (ORB-960): a Save as Version from an
 * editor that never finished loading stored `{}` over the real design. A blank canvas here would invite a save that
 * replaces the HTML that still works, so this offers the newest version that holds a real design instead.
 * "Revert to last version" can't: it only restores the latest version, which may be the empty one.
 */
export function EmptyDesignRecovery({ clientCompanyId, workspaceId, campaignId, assetId, versions, onRestored }: EmptyDesignRecoveryProps) {
    const [restoring, setRestoring] = useState(false)
    const { showSuccess, showError } = useToast()

    // Newest first, leaving out versions whose design is plainly empty; the server refuses any other empty one.
    const candidates = useMemo(
        () => versions
            .filter(v => v.FileSizeBytes > EMPTY_DESIGN_BYTES)
            .sort((a, b) => b.VersionNumber - a.VersionNumber),
        [versions],
    )
    const newest = candidates[0]

    const restore = useCallback(async () => {
        setRestoring(true)
        try {
            for (const version of candidates) {
                const response = await assetApi.loadVersionIntoDraft(clientCompanyId, workspaceId, campaignId, assetId, version.VersionNumber)
                if (!response.error) {
                    showSuccess('Design restored', `Restored version ${version.VersionNumber}. Check it, then save it as a version.`)
                    await onRestored()
                    return
                }
                // That version is empty too; try the next older one. Any other refusal stops here.
                if (!/design is empty/i.test(response.error)) {
                    showError('Restore failed', getDisplayErrorMessages(response.error).join(' '))
                    return
                }
            }
            showError('Nothing to restore', 'No saved version has a design. Ask Webster in the chat to recreate the template.')
        } finally {
            setRestoring(false)
        }
    }, [candidates, clientCompanyId, workspaceId, campaignId, assetId, onRestored, showSuccess, showError])

    return (
        <div role="alert" className="h-full flex items-center justify-center p-8">
            <div className="max-w-md text-center space-y-4">
                <AlertTriangle className="w-6 h-6 text-amber-500 mx-auto" />
                <div className="text-sm font-medium text-slate-800 dark:text-slate-100">This template's design is empty.</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                    It was saved while the editor was still loading, so there is nothing for the editor to open.
                    {newest
                        ? ' Restore the last saved version that has a design, or ask Webster in the chat to recreate the template.'
                        : ' No saved version has a design, so ask Webster in the chat to recreate the template.'}
                </div>
                {newest && (
                    <button
                        type="button"
                        onClick={restore}
                        disabled={restoring}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {restoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <History className="w-4 h-4" />}
                        Restore version {newest.VersionNumber}
                    </button>
                )}
            </div>
        </div>
    )
}
